import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  CreditCard,
  Home,
  ShieldCheck,
  Loader2,
  History,
} from 'lucide-react';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import airportImage from '../../assets/airportt.png';
import useWalletData from '../../hooks/useWalletData';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';

const formatCurrency = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value || 0));

const formatDate = (timestamp, t) => {
  if (!timestamp) return t('common.notAvailable');
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function WalletPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const {
    balance,
    transactions,
    accountDoc,
    loading,
    depositTotals,
    withdrawalTotals,
  } = useWalletData(true);

  const isLoading = initializing || loading;

  const stats = useMemo(
    () => [
      {
        label: t('wallet.totalDeposits'),
        value: formatCurrency(depositTotals.amount),
        sublabel: t('wallet.depositsCount', { count: depositTotals.count }),
        icon: ArrowUpRight,
        color: 'text-emerald-600',
        bg: 'bg-emerald-100',
      },
      {
        label: t('wallet.totalWithdrawals'),
        value: formatCurrency(withdrawalTotals.amount),
        sublabel: t('wallet.withdrawalsCount', {
          count: withdrawalTotals.count,
        }),
        icon: ArrowDownRight,
        color: 'text-rose-600',
        bg: 'bg-rose-100',
      },
      {
        label: t('wallet.transactions'),
        value: transactions.length,
        sublabel: t('wallet.lifetimeActivity'),
        icon: History,
        color: 'text-indigo-600',
        bg: 'bg-indigo-100',
      },
    ],
    [depositTotals, withdrawalTotals, transactions.length, t],
  );

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
              {t('wallet.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('wallet.loginDescription')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black dark:bg-slate-700 px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('common.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200">
                {t('wallet.badge')}
              </p>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('wallet.heroTitle')}
              </h1>
              <p className="text-xl text-yellow-50 mb-6 max-w-2xl">
                {t('wallet.heroDescription')}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-white/90">
                <span className="rounded-full border border-white/40 px-4 py-2 text-sm font-semibold">
                  {t('wallet.availableBalanceLabel', {
                    amount: formatCurrency(balance),
                  })}
                </span>
                <span className="rounded-full border border-white/40 px-4 py-2 text-sm font-semibold">
                  {t('wallet.totalTransactions', {
                    count: transactions.length,
                  })}
                </span>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Wallet Overview"
                className="w-full max-w-md h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        {isLoading ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-lg flex items-center justify-center border border-gray-100 dark:border-slate-800">
            <div className="flex items-center gap-3 text-gray-500 dark:text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('wallet.loading')}
            </div>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-12 w-12 rounded-2xl bg-black dark:bg-slate-700 flex items-center justify-center text-yellow-300 dark:text-yellow-400">
                    <Wallet className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                      {t('wallet.availableBalance')}
                    </p>
                    <p className="text-4xl font-black text-gray-900 dark:text-white">
                      {formatCurrency(balance)}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('wallet.balanceDescription')}
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <button
                    onClick={() => navigate('/wallet/add')}
                    className="rounded-2xl bg-yellow-400/90 dark:bg-yellow-500 px-4 py-3 text-sm font-semibold text-black dark:text-black transition hover:bg-yellow-300 dark:hover:bg-yellow-400"
                  >
                    {t('wallet.addMoney')}
                  </button>
                  <button
                    onClick={() => navigate('/wallet/withdraw')}
                    className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    {t('wallet.withdrawButton')}
                  </button>
                </div>
              </div>

              <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest mb-4">
                  {t('wallet.accountStatus')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4 border border-gray-100 dark:border-slate-800 rounded-2xl p-4">
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {t('wallet.depositAccount')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          {t('wallet.depositAccountStatus')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/wallet/deposit-account')}
                      className="text-sm font-semibold text-yellow-500 dark:text-yellow-400 hover:text-yellow-400 dark:hover:text-yellow-300"
                    >
                      {t('wallet.manage')}
                    </button>
                  </div>
                  <div className="flex items-start justify-between gap-4 border border-gray-100 dark:border-slate-800 rounded-2xl p-4">
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <Home className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {t('wallet.withdrawalAccount')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          {accountDoc?.stripeConnectAccountId
                            ? t('wallet.withdrawalAccountConnected', {
                                accountId:
                                  accountDoc.stripeConnectAccountId.slice(-6),
                              })
                            : t('wallet.withdrawalAccountNotConnected')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/wallet/withdrawal-account')}
                      className="text-sm font-semibold text-yellow-500 dark:text-yellow-400 hover:text-yellow-400 dark:hover:text-yellow-300"
                    >
                      {t('wallet.configure')}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {stats.map(stat => (
                <div
                  key={stat.label}
                  className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-100 dark:border-slate-800"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`h-12 w-12 rounded-2xl ${stat.bg} flex items-center justify-center`}
                    >
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-500 dark:text-slate-400">
                        {stat.label}
                      </p>
                      <p className="text-2xl font-black text-gray-900 dark:text-white">
                        {stat.value}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {stat.sublabel}
                  </p>
                </div>
              ))}
            </section>

            <section className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                    {t('wallet.recentTransactions')}
                  </p>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                    {t('wallet.activity')}
                  </h2>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/wallet/all-transactions')}
                    className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    {t('wallet.viewAll')}
                  </button>
                </div>
              </div>

              {transactions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 dark:border-slate-700 p-12 text-center">
                  <p className="text-gray-500 dark:text-slate-400">
                    {t('wallet.noTransactions')}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-slate-800">
                  {transactions.slice(0, 5).map(transaction => (
                    <button
                      key={transaction.id}
                      className="w-full flex items-center justify-between py-4 text-left transition hover:bg-gray-50 dark:hover:bg-slate-800 px-2 rounded-2xl"
                      onClick={() =>
                        navigate(`/wallet/transactions/${transaction.id}`, {
                          state: { transaction },
                        })
                      }
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`h-12 w-12 rounded-2xl flex items-center justify-center ${
                            transaction.type === 'deposit'
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                              : transaction.type === 'withdrawal'
                              ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
                              : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'
                          }`}
                        >
                          {transaction.type === 'deposit' ? (
                            <ArrowUpRight className="h-5 w-5" />
                          ) : transaction.type === 'withdrawal' ? (
                            <ArrowDownRight className="h-5 w-5" />
                          ) : (
                            <CreditCard className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {transaction.description ||
                              t('wallet.walletActivity')}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {formatDate(transaction.date, t)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-lg font-black ${
                            transaction.type === 'withdrawal' ||
                            transaction.type === 'payment'
                              ? 'text-rose-500 dark:text-rose-400'
                              : 'text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          {transaction.type === 'withdrawal' ||
                          transaction.type === 'payment'
                            ? '-'
                            : '+'}
                          {formatCurrency(Math.abs(transaction.amount || 0))}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wide">
                          {transaction.status || t('wallet.statusCompleted')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
